import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { Avatar } from '../../components/Avatar';
import { presenceText } from '../../i18n/presence';
import type { NodeId } from '../../lib/types';

/**
 * "Who's here" — the overlapping avatars in a screen's header.
 *
 * A COMPONENT rather than a region, because there is no single host: this appears in the top
 * bar and in the header of every full-screen menu, whose headers cover the top bar. The
 * vanilla version handled that by writing into every `.presence-stack` in the page with one
 * `querySelectorAll`, which is why the top-bar port had to leave an empty div for it to fill.
 * Each screen renders its own now, and that escape hatch is gone.
 *
 * ONE AVATAR PER PERSON, not per connection. Somebody with the map open in two tabs is one
 * person, and the tab that shows is the more active one — editing beats looking at something,
 * which beats sitting on the map — so their avatar reflects what they are actually doing
 * rather than whichever socket happened to connect last.
 */

export interface PresenceUser {
  userId: string;
  displayName?: string;
  initials?: string;
  color?: string;
  /**
   * The account picture's version, or null when there is none — the roster carries the VERSION
   * and never the bytes (see `presenceServer.ts`, and `components/Avatar.tsx` for what it is
   * turned into). Optional because a roster injected by a spec, or one from a server that
   * predates the column, simply draws initials.
   */
  avatarV?: number | null;
  loc?: { view?: string; nodeId?: NodeId | null; docId?: string | null; mode?: string };
}

/** Editing > looking at something > on the map. Used to pick which socket represents a user. */
function rank(u: PresenceUser): number {
  if (u.loc?.mode === 'edit') return 2;
  if (u.loc?.nodeId || u.loc?.docId) return 1;
  return 0;
}

/** How many avatars before it collapses into "+N". */
const MAX = 6;

/**
 * `id` exists only for the top bar's stack, which had one in the vanilla markup and which
 * other things still address by it. The rest are identified by the screen they are in.
 */
export function PresenceStack({ id }: { id?: string } = {}) {
  const s = useAppState();
  const myId = s.currentUser?.id;

  const byUser = new Map<string, PresenceUser>();
  for (const u of s.presenceRoster) {
    if (!u) continue;
    const prev = byUser.get(u.userId);
    if (!prev || rank(u) > rank(prev)) byUser.set(u.userId, u);
  }

  // Yourself first — rightmost, nearest the user chip, because the stack lays out reversed.
  const users = [...byUser.values()]
    // YOUR OWN avatar version comes from the session, not from the roster. A roster entry keeps
    // the version it had when that socket connected (presenceServer.ts says so and why), which
    // is fine for a colleague and wrong for you: an admin who has just set their own picture on
    // the permissions screen would otherwise watch the stack behind it not change. Everyone
    // else's still arrives on their next connect, which is the accepted trade.
    .map((u) =>
      u.userId === myId && s.currentUser ? { ...u, avatarV: s.currentUser.avatarV ?? null } : u,
    )
    .sort((a, b) => (a.userId === myId ? -1 : 0) - (b.userId === myId ? -1 : 0));

  const t = (k: Parameters<typeof presenceText>[0]) => presenceText(k, s.currentLocale);

  /** "editing “Orders”" / "viewing a document" / "on the map". */
  const activity = (u: PresenceUser): string => {
    const loc = u.loc || {};
    const verb = loc.mode === 'edit' ? t('editing') : t('viewing');
    if (loc.docId) {
      const title = s.docTitles[loc.docId];
      return `${verb} ${title ? `“${title}”` : t('adoc')}`;
    }
    if (loc.nodeId) {
      const n = s.nodes[loc.nodeId];
      return `${verb} ${n ? `“${n.label}”` : t('anode')}`;
    }
    return t('onmap');
  };

  const shown = users.slice(0, MAX);
  const rest = users.slice(MAX);

  return (
    <div className="presence-stack" id={id} title="Who's online">
      {shown.map((u) => (
        <Avatar
          key={u.userId}
          user={u}
          editing={u.loc?.mode === 'edit'}
          title={
            u.userId === myId
              ? `${u.displayName || ''} (${t('you')})`
              : `${u.displayName || ''} · ${activity(u)}`
          }
          // Plan 1.7: jump to where they are — their doc opens in VIEW mode (never their edit
          // mode), their node centres with its popup. The router refuses silently anywhere
          // this user may not follow; the tooltip above already says where they are. Your own
          // avatar goes nowhere: you are already there.
          onClick={
            u.userId === myId ? undefined : () => callAction('openPresenceLoc', u.loc || null)
          }
        />
      ))}
      {rest.length ? (
        <span className="avatar more" title={rest.map((u) => u.displayName).filter(Boolean).join(', ')}>
          +{rest.length}
        </span>
      ) : null}
    </div>
  );
}
